// src/auth/Login.js
import React, { useState } from "react";
import axios from "axios";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { API } from "../config/apiConfig";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

function Login({ onLogin }) {
  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [inactiveMessage, setInactiveMessage] = useState(""); // 👈 for popup
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setInactiveMessage("");
    setLoading(true);

    try {
      localStorage.removeItem("token"); // clear stale token

      // Add timeout for Azure SQL cold start (120 seconds for extra safety)
      const res = await axios.post(
        API.login,
        { usernameOrEmail, password, allowGpsBypass: true },
        { 
          headers: { "Content-Type": "application/json" },
          timeout: 120000 // 120 seconds timeout for Azure SQL warm-up
        }
      );

      if (res.data.success) {
        localStorage.setItem("token", res.data.token);
        onLogin();
        navigate("/dashboard");
      } else {
        setError(res.data.error || "Login failed");
      }
    } catch (err) {
      console.error("❌ Login error:", err.response?.data || err.message);

      if (err.response?.status === 403) {
        // 👇 backend returned inactive account
        setInactiveMessage(
          err.response?.data?.error ||
            "Your account is inactive. Please contact support."
        );
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        // 👇 timeout error - likely Azure SQL taking too long
        setError(
          "⏱️ Connection timeout (2 min). The database is waking up. Please click Login again to retry."
        );
      } else if (err.code === 'ERR_NETWORK' || !err.response) {
        // 👇 network error - server might be down
        setError(
          "🔌 Cannot connect to server. Please check your internet connection or try again later."
        );
      } else {
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            "❌ Server error while logging in"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack
      sx={{
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "center",
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.2) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Paper 
        elevation={24} 
        sx={{ 
          p: 5, 
          width: "100%", 
          maxWidth: 450,
          borderRadius: 3,
          bgcolor: 'background.paper',
          backdropFilter: 'blur(20px)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Typography 
          variant="h4" 
          gutterBottom 
          textAlign="center" 
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 3
          }}
        >
          Welcome Back
        </Typography>
        <Typography 
          variant="h6" 
          textAlign="center" 
          color="text.secondary"
          sx={{ mb: 4 }}
        >
          Sign in to your Dreamy Apps account
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleLogin}>
          <Stack spacing={2}>
            <TextField
              label="Username or Email"
              value={usernameOrEmail}
              onChange={(e) => setUsernameOrEmail(e.target.value)}
              required
              fullWidth
              disabled={loading}
            />

            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Login"
              )}
            </Button>

            {/* Loading message with Azure SQL info */}
            {loading && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Connecting to server... This may take up to 2 minutes if the database is waking up from idle state. Please be patient.
                </Typography>
              </Alert>
            )}

            {/* ✅ Forgot Password link */}
            <Typography variant="body2" textAlign="center">
              <Link component={RouterLink} to="/forgot-password" underline="hover">
                Forgot Password?
              </Link>
            </Typography>

            {/* ✅ Signup link */}
            <Typography variant="body2" textAlign="center">
              Not registered yet?{" "}
              <Link component={RouterLink} to="/signup" underline="hover">
                Sign up
              </Link>
            </Typography>
          </Stack>
        </form>
      </Paper>

      {/* 👇 Inactive User Popup */}
      <Dialog
        open={Boolean(inactiveMessage)}
        onClose={() => setInactiveMessage("")}
      >
        <DialogTitle>Account Inactive</DialogTitle>
        <DialogContent>
          <DialogContentText>{inactiveMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInactiveMessage("")} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export default Login;
